import { useState } from "react";
import { Card } from "@/components/ui-helpers";
import { WorkloadDrilldown } from "@/components/WorkloadDrilldown";
import { Users, AlertTriangle } from "lucide-react";

const Bar = ({ value, max, tone }) => (
  <div className="flex items-center gap-2 min-w-0">
    <div className="h-2 flex-1 rounded-full bg-slate-100 overflow-hidden">
      <div className={`h-full rounded-full ${tone}`} style={{ width: `${max && value ? Math.max(4, (value / max) * 100) : 0}%` }} />
    </div>
    <span className="w-7 text-right font-semibold text-slate-800 tabular-nums">{value}</span>
  </div>
);

export function TeamWorkloadCard({ rows, onChanged }) {
  const [drill, setDrill] = useState(null);
  const max = Math.max(1, ...rows.map((r) => r.open_cases));
  const avg = rows.length ? rows.reduce((s, r) => s + r.open_cases, 0) / rows.length : 0;
  const load = (r) => (r.open_cases === 0 ? ["Free", "text-emerald-700 bg-emerald-50 border-emerald-200"]
    : avg && r.open_cases > avg * 1.5 ? ["Heavy", "text-rose-700 bg-rose-50 border-rose-200"]
    : ["Balanced", "text-slate-600 bg-slate-50 border-slate-200"]);
  return (
    <Card className="p-5 mt-4" data-testid="team-workload-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2"><Users className="h-4 w-4 text-primary" /><h3 className="font-head font-bold text-slate-900">Team workload</h3></div>
        <span className="text-xs text-slate-500" data-testid="team-workload-avg">Average {avg.toFixed(1)} open cases per person</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm table-fixed">
          <colgroup><col className="w-[22%]" /><col className="w-[27%]" /><col className="w-[10%]" /><col className="w-[13%]" /><col className="w-[12%]" /><col className="w-[16%]" /></colgroup>
          <thead className="text-xs uppercase tracking-wider text-slate-500">
            <tr className="border-b border-slate-100">
              <th className="text-left py-2 font-semibold">Staff</th><th className="text-left py-2 font-semibold">Open cases</th>
              <th className="text-right py-2 font-semibold">Clients</th><th className="text-right py-2 font-semibold">Open tasks</th>
              <th className="text-right py-2 font-semibold">Follow-ups</th><th className="text-right py-2 font-semibold">Load</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {rows.map((r, i) => {
              const [label, cls] = load(r);
              return (
                <tr key={r.id} data-testid={`workload-row-${r.id}`}>
                  <td className="py-2.5 pr-3"><div className="flex items-center gap-2 min-w-0">
                    <span className="h-6 w-6 rounded-full bg-secondary text-white text-[11px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                    <button type="button" data-testid={`workload-open-${r.id}`} onClick={() => setDrill({ person: r, reassign: false })} className="truncate font-medium text-slate-800 hover:text-primary hover:underline text-left" title={`See ${r.name}'s cases, tasks and follow-ups`}>{r.name}</button>
                    {r.role === "admin" && <span className="text-[10px] text-slate-400">admin</span>}
                  </div></td>
                  <td className="py-2.5 pr-4"><Bar value={r.open_cases} max={max} tone="bg-primary" /></td>
                  <td className="py-2.5 text-right tabular-nums text-slate-600">{r.clients}</td>
                  <td className="py-2.5 text-right tabular-nums text-slate-600">
                    {r.open_tasks}{r.overdue_tasks > 0 && <span className="ml-1 inline-flex items-center gap-0.5 text-[11px] font-semibold text-rose-600" title={`${r.overdue_tasks} overdue`}><AlertTriangle className="h-3 w-3" />{r.overdue_tasks}</span>}
                  </td>
                  <td className={`py-2.5 text-right tabular-nums ${r.followups_due ? "text-amber-600 font-semibold" : "text-slate-600"}`}>{r.followups_due}</td>
                  <td className="py-2.5 text-right">
                    {label === "Heavy" ? (
                      <button type="button" data-testid={`workload-rebalance-${r.id}`} onClick={() => setDrill({ person: r, reassign: true })} title="Move some cases to someone with free capacity"
                        className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border hover:shadow-sm ${cls}`}>Heavy · Rebalance</button>
                    ) : <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>{label}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400 mt-3">Click a name to see their open cases, tasks and follow-ups. Open cases count each case where the person is the consultant or assigned staff. "Heavy" means more than 1.5× the team average.</p>
      {drill && <WorkloadDrilldown person={drill.person} team={rows} startReassign={drill.reassign} onClose={() => setDrill(null)} onChanged={onChanged} />}
    </Card>
  );
}
